/**
 * Pixel primitives of the FX sample pages (tools/fxSamples, lane FX-PIXEL P3 / FX-TAIJI), shared with the game's
 * ported skill effects (lane FX-PORT, render/fx/sampleArt/*): drawn cell by cell on the FX pen (render/fx/pixelRules.ts
 * `PixelPen`), one cell = one scene art px. Colours come straight from `FX_RAMPS`; fading is a dither level (`DITHER`),
 * never a blend. Shapes 2+ cells thick get the pen's 1-px dark outline; 1-px lines stay bare glints (the pen's rule).
 * Moved here unchanged from tools/fxSamples/kit.ts (which re-exports it).
 */
import { FX_RAMPS } from '../pixelRules';

export type G = CanvasRenderingContext2D;
export type V = readonly [number, number];
export type Ramp = readonly string[];

export const R = FX_RAMPS as Record<string, Ramp>;
/** Dither levels as the pen reads alpha: gone, 25 % grid, 50 % checker, solid. */
export const DITHER = [0, 0.08, 0.2, 1] as const;

export const add = (a: V, b: V, k = 1): V => [a[0] + b[0] * k, a[1] + b[1] * k];
export const lerp = (a: V, b: V, u: number): V => [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u];
export const len = (a: V): number => Math.hypot(a[0], a[1]);
export const unit = (a: V): V => {
  const l = len(a) || 1;
  return [a[0] / l, a[1] / l];
};
/** The perpendicular on the upper side of a direction (screen y down). */
export const perp = (d: V): V => (d[0] >= 0 ? [d[1], -d[0]] : [-d[1], d[0]]);

/** Sets the dither level (0..3) for what follows. */
export function level(g: G, lvl: number): void {
  g.globalAlpha = DITHER[Math.max(0, Math.min(3, Math.round(lvl)))]!;
}

/** One cell. */
export function px(g: G, x: number, y: number, css: string): void {
  g.fillStyle = css;
  g.fillRect(Math.round(x), Math.round(y), 1, 1);
}

/** A 1-cell line (DDA on the grid, one cell per major step: a clean stepped line, no doubles). */
export function line(g: G, a: V, b: V, css: string): void {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const n = Math.max(1, Math.round(Math.max(Math.abs(dx), Math.abs(dy))));
  g.fillStyle = css;
  for (let i = 0; i <= n; i++) g.fillRect(Math.round(a[0] + (dx * i) / n), Math.round(a[1] + (dy * i) / n), 1, 1);
}

/**
 * A blade of light from `a` to `b`: a 1-cell `core`, and where the width profile asks (1 -> `w` -> 1 along the line,
 * peak at `peak`), extra rows in `edge` on the lower side (light from the upper right: the lit core is on top).
 */
export function blade(g: G, a: V, b: V, w: number, core: string, edge: string, peak = 0.5): void {
  const d = unit([b[0] - a[0], b[1] - a[1]]);
  const down: V = [-perp(d)[0], -perp(d)[1]];
  const n = Math.max(1, Math.round(len([b[0] - a[0], b[1] - a[1]])));
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const prof = u < peak ? u / peak : (1 - u) / (1 - peak);
    const rows = Math.max(1, Math.round(1 + (w - 1) * Math.min(1, prof * 1.4)));
    const p = lerp(a, b, u);
    for (let k = rows - 1; k >= 1; k--) px(g, p[0] + down[0] * k, p[1] + down[1] * k, edge);
    px(g, p[0], p[1], core);
  }
}

/** A spark: a diamond of radius `r` (r 2 = sample B's 5-px diamond) with a light core; r 1 is a plus. */
export function diamond(g: G, x: number, y: number, r: number, body: string, core: string): void {
  x = Math.round(x);
  y = Math.round(y);
  g.fillStyle = body;
  for (let j = -r; j <= r; j++) for (let i = -r; i <= r; i++) if (Math.abs(i) + Math.abs(j) <= r) g.fillRect(x + i, y + j, 1, 1);
  px(g, x, y, core);
}

/** An iso ring on the ground (2:1), 1 cell thick (2 when `thick`). */
export function isoRing(g: G, x: number, y: number, rx: number, css: string, thick = false, from = 0, to = Math.PI * 2): void {
  const n = Math.max(12, Math.round(rx * 7));
  g.fillStyle = css;
  for (let i = 0; i <= n; i++) {
    const a = from + ((to - from) * i) / n;
    const cx = Math.round(x + Math.cos(a) * rx);
    const cy = Math.round(y + Math.sin(a) * rx * 0.5);
    g.fillRect(cx, cy, 1, thick ? 2 : 1);
  }
}

/**
 * A thin crescent round (cx, cy), radius `r`, from angle a0 to a1 (screen angles), squashed by `sy` (1 upright, 0.5 on
 * the ground): `core` on the outer row, `edge` rows inside, the width 1 -> w -> 1 along it, the leading end (a1) the
 * thick one when `lead` (a fresh cut), else even.
 */
export function crescent(
  g: G,
  cx: number,
  cy: number,
  r: number,
  a0: number,
  a1: number,
  w: number,
  core: string,
  edge: string,
  sy = 1,
  lead = true,
): void {
  const n = Math.max(8, Math.round(Math.abs(a1 - a0) * r * 1.6));
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const prof = lead ? Math.min(1, u * 1.6) * Math.min(1, (1 - u) * 5) : Math.sin(Math.PI * u);
    const rows = Math.max(1, Math.round(1 + (w - 1) * prof));
    const a = a0 + (a1 - a0) * u;
    for (let k = rows - 1; k >= 0; k--)
      px(g, cx + Math.cos(a) * (r - k), cy + Math.sin(a) * (r - k) * sy, k === 0 ? core : edge);
  }
}

/**
 * A sword plotted cell by cell (no picture rotation): tip at `tip`, pointing along `u`; blade `L` cells, 2 wide with
 * the lit edge on the side facing the upper right, a gold guard, a dark hilt and a red tassel hanging down.
 */
export function sword(g: G, tip: V, u: V, L = 13): void {
  u = unit(u);
  const n0 = perp(u);
  // The side of the blade facing the light (up and to the right).
  const lit = n0[0] - n0[1] >= 0 ? n0 : ([-n0[0], -n0[1]] as V);
  const shade: V = [-lit[0], -lit[1]];
  const S = R.steel!;
  const W = R.white!;
  const Gd = R.gold!;
  const B = R.brown!;
  const M = R.maroon!;
  for (let s = 0; s <= L; s++) {
    const p = add(tip, u, -s);
    if (s >= 2) px(g, p[0] + shade[0], p[1] + shade[1], S[3]!);
    px(g, p[0], p[1], s === 0 ? W[8]! : S[5]!);
  }
  const guard = add(tip, u, -L - 1);
  for (let k = -2; k <= 2; k++) px(g, guard[0] + n0[0] * k, guard[1] + n0[1] * k, k === 0 ? Gd[5]! : Gd[4]!);
  for (let s = 2; s <= 4; s++) {
    const p = add(tip, u, -L - s);
    px(g, p[0], p[1], B[4]!);
  }
  const end = add(tip, u, -L - 5);
  px(g, end[0], end[1], Gd[4]!);
  px(g, end[0], end[1] + 1, M[4]!);
  px(g, end[0] + 1, end[1] + 2, M[5]!);
  px(g, end[0], end[1] + 3, M[4]!);
}

/** A deterministic 0..1 hash (the page has no game rules; plain sampling, not `Math.random`, so loops repeat). */
export function hash(i: number, j = 0): number {
  let h = (i * 374761393 + j * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
